import React, { useState, useEffect, useRef, useCallback } from "react";
import { observer } from "mobx-react-lite";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  CContainer,
  CCard,
  CCardHeader,
  CCardBody,
  CCardFooter,
  CButton,
  CFormTextarea,
  CSpinner,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilChatBubble, cilSend, cilTrash } from "@coreui/icons";
import settingsStore from "../../store/SettingsStore";
import ServerSelect from "../UI/PeriodFilters/ServerSelect";
import ErrorAlert from "../UI/Status/ErrorAlert";
import toastStore from "../../store/ToastStore";
import { sendAssistantMessage, getAssistantHistory, clearAssistantHistory } from "../../api/assistantApi";
import { logReportView } from "../../api/reportLogApi";
import ToolResultsPanel from "./ToolResultView";

const SUGGESTIONS = [
  "Как дела с выручкой за последнюю неделю?",
  "Кто из официантов лидирует, а кто отстаёт?",
  "Какие блюда стоит вывести из меню?",
  "Были ли опасные кассовые операции за последние 3 дня?",
];

function MessageBubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`d-flex mb-2 ${isUser ? "justify-content-end" : "justify-content-start"}`}>
      <div
        className={`p-2 px-3 rounded-3 ${isUser ? "bg-primary text-white" : "bg-body-secondary"}`}
        style={{ maxWidth: "75%", whiteSpace: isUser ? "pre-wrap" : "normal" }}
      >
        {isUser ? (
          content
        ) : (
          <div className="assistant-markdown">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}

const AssistantPage = observer(() => {
  const [selectedServer, setSelectedServer] = useState("");
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    const init = async () => {
      const userId = localStorage.getItem("userId");
      if (userId) await settingsStore.getServers(userId);
      logReportView({ event: "report_view", reportKey: "assistant", reportTitle: "AI-помощник", userId, path: "/assistant" });
    };
    init();
  }, []);

  useEffect(() => {
    if (!settingsStore.servers?.length) return;
    if (selectedServer === "") {
      setSelectedServer(String(settingsStore.servers[0].id));
    }
  }, [settingsStore.servers, selectedServer]);

  const loadHistory = useCallback(async (serverId) => {
    if (!serverId) return;
    setHistoryLoading(true);
    setError(null);
    try {
      const { history } = await getAssistantHistory(serverId);
      setMessages(history || []);
    } catch (e) {
      setError(e);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedServer) loadHistory(selectedServer);
  }, [selectedServer, loadHistory]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const handleSend = async (text) => {
    const value = (text ?? draft).trim();
    if (!value || !selectedServer || loading) return;
    setDraft("");
    setError(null);
    setMessages((prev) => [...prev, { role: "user", content: value }]);
    setLoading(true);
    try {
      const { reply, toolResults } = await sendAssistantMessage(selectedServer, value);
      setMessages((prev) => [...prev, { role: "assistant", content: reply, toolResults }]);
    } catch (e) {
      setError(e);
      toastStore.error(e?.response?.data?.message || "Не удалось получить ответ от AI-помощника.");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    if (!selectedServer) return;
    try {
      await clearAssistantHistory(selectedServer);
      setMessages([]);
      toastStore.success("История очищена.");
    } catch (e) {
      toastStore.error("Не удалось очистить историю.");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <CContainer style={{ flex: "1", marginTop: "150px" }}>
      <h4 className="mb-3">
        <CIcon icon={cilChatBubble} className="me-2" />
        AI-помощник
      </h4>
      <ServerSelect
        servers={settingsStore.servers}
        value={selectedServer}
        onChange={(e) => setSelectedServer(e.target.value)}
        id="assistantServerSelect"
      />

      {!selectedServer ? null : (
        <CCard className="mb-4">
          <CCardHeader className="d-flex align-items-center justify-content-between py-2">
            <span className="fw-semibold">Диалог</span>
            <CButton size="sm" color="secondary" variant="outline" onClick={handleClear} disabled={messages.length === 0}>
              <CIcon icon={cilTrash} className="me-1" />
              Очистить историю
            </CButton>
          </CCardHeader>
          <CCardBody
            ref={scrollRef}
            style={{ height: "68vh", overflowY: "auto" }}
          >
            <ErrorAlert error={error} title="Ошибка" />

            {historyLoading && (
              <div className="d-flex align-items-center gap-2 text-body-secondary py-2">
                <CSpinner size="sm" />
                Загрузка истории…
              </div>
            )}

            {!historyLoading && messages.length === 0 && (
              <div className="text-body-secondary py-2">
                <p>Задайте вопрос о продажах, официантах, меню или кассовой дисциплине — помощник сам запросит нужные данные из iiko.</p>
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {SUGGESTIONS.map((s) => (
                    <CButton key={s} size="sm" color="primary" variant="outline" onClick={() => handleSend(s)}>
                      {s}
                    </CButton>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <React.Fragment key={i}>
                <MessageBubble role={m.role} content={m.content} />
                {m.role === "assistant" && <ToolResultsPanel toolResults={m.toolResults} serverId={selectedServer} />}
              </React.Fragment>
            ))}

            {loading && (
              <div className="d-flex align-items-center gap-2 text-body-secondary py-2">
                <CSpinner size="sm" />
                Помощник запрашивает данные и думает над ответом…
              </div>
            )}
          </CCardBody>
          <CCardFooter>
            <div className="d-flex gap-2">
              <CFormTextarea
                rows={2}
                placeholder="Напишите вопрос…"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={loading}
              />
              <CButton color="primary" onClick={() => handleSend()} disabled={loading || !draft.trim()}>
                <CIcon icon={cilSend} />
              </CButton>
            </div>
          </CCardFooter>
        </CCard>
      )}
    </CContainer>
  );
});

export default AssistantPage;
